import { supabase } from './supabaseClient';
import { AuthService } from './authService';

async function adminId() {
  const user = await AuthService.getCurrentUser();
  return user?.id || null;
}

export class EnterpriseAdminService {
  static async list() {
    const { data, error } = await supabase.rpc('admin_enterprise_list', {
      p_admin_user_id: await adminId(),
    });
    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || 'Failed to load enterprises');
    return data.items || [];
  }

  static async get(enterpriseId) {
    const { data, error } = await supabase.rpc('admin_enterprise_get', {
      p_admin_user_id: await adminId(),
      p_enterprise_id: enterpriseId,
    });
    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || 'Enterprise not found');
    return data;
  }

  static async setStatus(enterpriseId, status) {
    const { data, error } = await supabase.rpc('admin_enterprise_set_status', {
      p_admin_user_id: await adminId(),
      p_enterprise_id: enterpriseId,
      p_status: status,
    });
    if (error) throw error;
    return data;
  }

  static async setSubscription(enterpriseId, { monthlyInr, autopay }) {
    const { data, error } = await supabase.rpc('admin_enterprise_set_subscription', {
      p_admin_user_id: await adminId(),
      p_enterprise_id: enterpriseId,
      p_monthly_inr: Number(monthlyInr || 0),
      p_autopay: autopay !== false,
    });
    if (error) throw error;
    return data;
  }

  static async upsert(payload) {
    const { data, error } = await supabase.rpc('admin_enterprise_upsert', {
      p_admin_user_id: await adminId(),
      p_payload: payload,
    });
    if (error) throw error;
    return data;
  }

  static async createUser(enterpriseId, { name, email, password, phone, isOwner }) {
    const { data, error } = await supabase.rpc('admin_enterprise_create_user', {
      p_admin_user_id: await adminId(),
      p_enterprise_id: enterpriseId,
      p_name: name,
      p_email: email,
      p_password: password,
      p_phone: phone || null,
      p_is_owner: !!isOwner,
    });
    if (error) throw error;
    return data;
  }

  static async setUserStatus(enterpriseUserId, status) {
    const { data, error } = await supabase.rpc('admin_enterprise_set_user_status', {
      p_admin_user_id: await adminId(),
      p_enterprise_user_id: enterpriseUserId,
      p_status: status,
    });
    if (error) throw error;
    return data;
  }

  static async walletAdjust(enterpriseId, amount, reason) {
    const { data, error } = await supabase.rpc('admin_enterprise_wallet_adjust', {
      p_admin_user_id: await adminId(),
      p_enterprise_id: enterpriseId,
      p_amount: amount,
      p_reason: reason || null,
    });
    if (error) throw error;
    return data;
  }

  static async topupApprove(topupId, approve = true) {
    const { data, error } = await supabase.rpc('admin_enterprise_topup_approve', {
      p_admin_user_id: await adminId(),
      p_topup_id: topupId,
      p_approve: approve,
    });
    if (error) throw error;
    return data;
  }

  static async apiKeyCreate(enterpriseId, label) {
    const { data, error } = await supabase.rpc('admin_enterprise_api_key_create', {
      p_admin_user_id: await adminId(),
      p_enterprise_id: enterpriseId,
      p_label: label || 'API key',
    });
    if (error) throw error;
    return data;
  }

  static async apiKeyRevoke(keyId) {
    const { data, error } = await supabase.rpc('admin_enterprise_api_key_revoke', {
      p_admin_user_id: await adminId(),
      p_key_id: keyId,
    });
    if (error) throw error;
    return data;
  }

  static async complaints(enterpriseId, limit = 100, offset = 0) {
    const { data, error } = await supabase.rpc('admin_enterprise_complaints', {
      p_admin_user_id: await adminId(),
      p_enterprise_id: enterpriseId,
      p_limit: limit,
      p_offset: offset,
    });
    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || 'Failed to load complaints');
    return data;
  }

  static async ledger(enterpriseId, limit = 200) {
    const { data, error } = await supabase.rpc('admin_enterprise_ledger', {
      p_admin_user_id: await adminId(),
      p_enterprise_id: enterpriseId,
      p_limit: limit,
    });
    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || 'Failed to load ledger');
    return data;
  }

  static async citiesForState(state) {
    const st = String(state || '').trim();
    if (!st) return [];
    const { data, error } = await supabase
      .from('pincode_city_state')
      .select('district')
      .ilike('state', st)
      .not('district', 'is', null)
      .limit(4000);
    if (error) return [];
    const seen = new Set();
    const out = [];
    for (const row of data || []) {
      const name = String(row.district || '').trim();
      if (!name) continue;
      const k = name.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(name);
    }
    return out.sort((a, b) => a.localeCompare(b));
  }

  static async ratesGet(enterpriseId) {
    const { data, error } = await supabase.rpc('admin_enterprise_rates_get', {
      p_admin_user_id: await adminId(),
      p_enterprise_id: enterpriseId,
    });
    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || 'Failed to load rates');
    return data;
  }

  static async ratesUpsert(enterpriseId, { scope, state, city, rows }) {
    const { data, error } = await supabase.rpc('admin_enterprise_rates_upsert', {
      p_admin_user_id: await adminId(),
      p_enterprise_id: enterpriseId,
      p_scope: scope,
      p_state: state || null,
      p_city: city || null,
      p_rows: rows || [],
    });
    if (error) throw error;
    return data;
  }

  static async ratesDeleteScope(enterpriseId, { scope, state, city }) {
    const { data, error } = await supabase.rpc('admin_enterprise_rates_delete_scope', {
      p_admin_user_id: await adminId(),
      p_enterprise_id: enterpriseId,
      p_scope: scope,
      p_state: state || null,
      p_city: city || null,
    });
    if (error) throw error;
    return data;
  }

  static async rateSettings(enterpriseId, { dayStart, dayEnd, allowInstantAccept }) {
    const { data, error } = await supabase.rpc('admin_enterprise_rate_settings', {
      p_admin_user_id: await adminId(),
      p_enterprise_id: enterpriseId,
      p_day_start: dayStart || null,
      p_day_end: dayEnd || null,
      p_allow_instant_accept:
        typeof allowInstantAccept === 'boolean' ? allowInstantAccept : null,
    });
    if (error) throw error;
    return data;
  }

  static async apiLogs(enterpriseId, limit = 200) {
    const { data, error } = await supabase.rpc('admin_enterprise_api_logs', {
      p_admin_user_id: await adminId(),
      p_enterprise_id: enterpriseId,
      p_limit: limit,
    });
    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || 'Failed to load logs');
    return data.logs || [];
  }
}

export default EnterpriseAdminService;
